// @ts-check
// Un borrador por ejercicio, no uno por editor.
//
// ## El fallo que cierra este módulo
//
// Los tres editores guardaban su autoguardado en UNA sola clave por usuario,
// sin distinguir de qué ejercicio era el trabajo:
//
//     progedu.diagram-editor.autoguardado
//
// Dos consecuencias, las dos reales y las dos silenciosas:
//
//   1. Traducir de Python al diagrama MACHACABA el diagrama del ejercicio que
//      el alumno estuviera haciendo. Sin aviso y sin vuelta atrás.
//   2. Los ejercicios compartían lienzo: cambiar de ejercicio en el selector
//      no cambiaba el programa, porque solo había uno.
//
// Lo destapó el usuario pidiendo que, al abrir el editor desde una
// traducción, el selector volviera a "Elige ejercicio" (2026-08-06). El
// selector era el síntoma; esto es la causa.
//
// ## El ámbito
//
// Cada borrador vive ahora en su propio hueco, identificado por el ejercicio
// que se está haciendo -- o por `AMBITO_LIBRE` cuando no se está haciendo
// ninguno, que es el caso de una traducción recién abierta y el de quien
// entra al editor a trastear.
//
// El separador es `::` y no un punto porque el punto ya separa el `usuarioId`
// en la clave del editor de diagramas: mezclarlos haría imposible leer una
// clave y saber dónde acaba el usuario y empieza el ejercicio.
//
// ## Por qué vive en `state/` y no en cada feature
//
// Las tres features tienen que calcular la MISMA clave, y ninguna puede
// importar a las otras. Si cada una compusiera la suya, bastaría con que una
// cambiara el separador para que sus borradores dejaran de encontrarse -- sin
// ningún error, solo un editor que aparece vacío. La clave BASE sigue siendo
// un literal propio de cada feature (`editor-drafts-namespaces.spec.js`
// necesita encontrarlo escrito para exigir que la limpieza de sesión lo
// cubra); lo compartido es solo cómo se le añade el ámbito.
//
// La limpieza al iniciar/cerrar sesión (`state/editor-drafts.js`) sigue
// funcionando sin tocar nada: busca por PREFIJO, y estas claves empiezan
// exactamente igual que antes.

/**
 * Ámbito de quien no está haciendo ningún ejercicio: el borrador libre.
 *
 * No es `null` ni la clave sin sufijo a propósito. La clave sin sufijo es la
 * de ANTES de esta división, y hace falta poder distinguirla para migrarla
 * una sola vez (ver `migrarBorradorHeredado`).
 */
export const AMBITO_LIBRE = 'libre';

/** Separador entre la clave base y el ámbito. */
const SEPARADOR = '::';

/**
 * Normaliza un slug de ejercicio a un ámbito utilizable.
 * @param {string | null | undefined} slug
 * @returns {string} El slug recortado, o `AMBITO_LIBRE` si no hay ninguno.
 */
export function ambitoDeEjercicio(slug) {
  if (typeof slug !== 'string') return AMBITO_LIBRE;
  const recortado = slug.trim();
  return recortado.length > 0 ? recortado : AMBITO_LIBRE;
}

/**
 * Clave de almacenamiento de un borrador dentro de su ámbito.
 * @param {string} claveBase Clave del editor (con su `usuarioId` si lo lleva).
 * @param {string | null} [ambito] Slug del ejercicio. Sin él, el borrador libre.
 * @returns {string}
 */
export function claveConAmbito(claveBase, ambito) {
  return `${claveBase}${SEPARADOR}${ambitoDeEjercicio(ambito)}`;
}

/**
 * @returns {Storage | null}
 */
function obtenerStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Safari en modo privado y navegadores con el almacenamiento bloqueado
    // lanzan al ACCEDER a la propiedad, no solo al escribir.
    return null;
  }
}

/**
 * Adopta el borrador único que quedó de antes de esta división, si lo hay.
 *
 * Lo mueve al ámbito indicado y borra la clave antigua, así que solo actúa
 * la primera vez. Si el ámbito de destino ya tiene un borrador propio, la
 * clave antigua se descarta sin pisarlo: lo nuevo manda.
 *
 * A qué ámbito va lo decide QUIEN LLAMA, no este módulo: la decisión del
 * usuario (2026-08-06) fue asignarlo al último ejercicio abierto, que es un
 * dato que solo la página conoce (lo recuerda su propio `localStorage`). Sin
 * ese dato, `AMBITO_LIBRE`.
 *
 * @param {string} claveBase Clave del editor (con su `usuarioId` si lo lleva).
 * @param {string | null | undefined} ambitoDestino
 * @returns {boolean} `true` si se adoptó algo (útil para tests y depuración).
 */
export function migrarBorradorHeredado(claveBase, ambitoDestino) {
  const storage = obtenerStorage();
  if (!storage) return false;

  let heredado;
  try {
    heredado = storage.getItem(claveBase);
  } catch {
    return false;
  }
  if (heredado === null) return false;

  const destino = claveConAmbito(claveBase, ambitoDestino);
  try {
    // Nunca pisa un borrador ya existente en el destino: si el alumno ya ha
    // trabajado en ese ejercicio bajo el esquema nuevo, eso es lo bueno.
    if (storage.getItem(destino) === null) storage.setItem(destino, heredado);
    storage.removeItem(claveBase);
    return true;
  } catch {
    // Cuota llena o almacenamiento bloqueado: se deja la clave antigua
    // intacta para reintentarlo en la próxima carga. Nunca se borra sin
    // haber escrito la copia.
    return false;
  }
}
